/**
 * Warm, unisex silhouette portrait used as a fallback whenever a
 * memorial (or member profile) has no photo. Deliberately gender-neutral
 * and stylised so it reads as "portrait placeholder" rather than a
 * specific person.
 *
 * Returned as an SVG data URL so it renders in an <img src=…> without
 * hitting the network or object storage.
 */
export const SILHOUETTE_SVG_DATA_URL =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400" role="img" aria-label="Portrait placeholder">
  <defs>
    <radialGradient id="bg" cx="50%" cy="35%" r="75%">
      <stop offset="0%" stop-color="#FFE9C7"/>
      <stop offset="60%" stop-color="#F6C583"/>
      <stop offset="100%" stop-color="#C2410C"/>
    </radialGradient>
    <linearGradient id="fig" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%"  stop-color="#3D2A17" stop-opacity="0.95"/>
      <stop offset="100%" stop-color="#1E140A" stop-opacity="1"/>
    </linearGradient>
  </defs>
  <rect width="400" height="400" fill="url(#bg)"/>
  <!-- Soft warm ring for depth -->
  <circle cx="200" cy="200" r="185" fill="none" stroke="#FFFFFF" stroke-opacity="0.12" stroke-width="4"/>
  <!-- Head + shoulders silhouette (unisex, softly rounded) -->
  <g fill="url(#fig)">
    <!-- Head -->
    <ellipse cx="200" cy="160" rx="66" ry="78"/>
    <!-- Neck -->
    <rect x="180" y="220" width="40" height="30" rx="10"/>
    <!-- Shoulders / torso -->
    <path d="M 60 400 C 65 320, 120 260, 200 260 C 280 260, 335 320, 340 400 Z"/>
  </g>
</svg>`);

/**
 * `<img>` fallback helper — attach as an onError handler to auto-swap
 * to the silhouette when a remote portrait URL 404s.
 *
 *   <img src={m.portrait} onError={silhouetteOnError} />
 */
export function silhouetteOnError(e) {
  const el = e.currentTarget;
  if (el && el.src !== SILHOUETTE_SVG_DATA_URL) {
    el.src = SILHOUETTE_SVG_DATA_URL;
  }
}

// Gendered variants for the memorial-card photo frame placeholder.
const _svg = (body) =>
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400" role="img" aria-label="Portrait placeholder">
  <defs>
    <radialGradient id="bg" cx="50%" cy="35%" r="80%">
      <stop offset="0%" stop-color="#F7F3EC"/>
      <stop offset="70%" stop-color="#EAE2D3"/>
      <stop offset="100%" stop-color="#D8CCB6"/>
    </radialGradient>
    <linearGradient id="fig" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#8A7A63" stop-opacity="0.95"/>
      <stop offset="100%" stop-color="#5F5240" stop-opacity="1"/>
    </linearGradient>
  </defs>
  <rect width="400" height="400" fill="url(#bg)"/>
  <circle cx="200" cy="200" r="186" fill="none" stroke="#FFFFFF" stroke-opacity="0.35" stroke-width="4"/>
  <g fill="url(#fig)">${body}</g>
</svg>`);

export const MALE_SILHOUETTE_SVG_DATA_URL = _svg(`
    <path d="M 134 148 C 134 100, 162 78, 200 78 C 238 78, 266 100, 266 148 L 262 150 C 250 122, 238 116, 200 116 C 162 116, 150 122, 138 150 Z"/>
    <ellipse cx="200" cy="168" rx="62" ry="72"/>
    <rect x="181" y="224" width="38" height="30" rx="10"/>
    <path d="M 62 400 C 68 322, 122 264, 200 264 C 278 264, 332 322, 338 400 Z"/>`);

export const FEMALE_SILHOUETTE_SVG_DATA_URL = _svg(`
    <path d="M 200 74 C 148 74, 122 112, 122 168 C 122 224, 132 262, 118 300 C 150 292, 158 268, 160 240 L 240 240 C 242 268, 250 292, 282 300 C 268 262, 278 224, 278 168 C 278 112, 252 74, 200 74 Z" opacity="0.55"/>
    <ellipse cx="200" cy="168" rx="58" ry="70"/>
    <rect x="182" y="224" width="36" height="28" rx="10"/>
    <path d="M 66 400 C 72 324, 126 266, 200 266 C 274 266, 328 324, 334 400 Z"/>`);

export function genderSilhouette(gender) {
  const g = String(gender || "").toLowerCase();
  if (g === "male" || g === "m" || g === "man") return MALE_SILHOUETTE_SVG_DATA_URL;
  if (g === "female" || g === "f" || g === "woman") return FEMALE_SILHOUETTE_SVG_DATA_URL;
  return SILHOUETTE_SVG_DATA_URL;
}
